import { ROUTES } from "../../constant";
import {
  BOOKING_IN_PROGRESS_BREADCRUMBS_TEXT,
  INITIAL_BREADCRUMBS_LIST,
} from "../../constant/bookingInProgressConstant/breadCrumbsConstant";
import { setItemInLocalStorage } from "../../hooks/localStorage";
import { getItemFromSessionStorage, setItemInSessionStorage } from "../../hooks/sessionStorage";

/**
 * Guard against a hard (re)load stranding the user on a dead booking review.
 *
 * The booking wizard's *position* is persisted (`_zenvoya_active_breadcrumbs`,
 * `screen_details` via useSessionStorageV1; `_zenvoya_breadcrumbs_list` via the
 * localStorage-backed useSessionStorage), so it survives a refresh. But the
 * wizard's *working state* past the search step is entirely ephemeral —
 * `reviewSteps`, the traveller form, the Stripe form (all component useState)
 * and the backend prebook/cart session (short-lived, gone the moment the API
 * server restarts). So restoring `activeBreadCrumbs = "ReviewNew"` after a hard
 * reload re-mounts ReviewTravelersPaymentCrumbsDetails with nothing to render →
 * a blank body, and `useReviewLeaveBackTrap` swallows browser Back into the
 * "Leave the review cart?" dialog → the user is stuck with no way out.
 *
 * Repro that surfaced this: on the review page the API server goes away (a
 * network error trips the maintenance screen and the session ends); after a
 * refresh + re-login the persisted `ReviewNew` crumb is restored, blank and
 * exit-less. See the split-brain analysis in the booking-review flow.
 *
 * Fix: on boot — BEFORE any React code reads booking state — if the persisted
 * active crumb is a non-resumable step, roll the wizard back to the first step
 * and drop the booking-panel screen so the user lands on Home. A one-shot flag
 * lets the app surface a "your booking session expired" toast once it mounts.
 *
 * Must be called synchronously at app entry (index.tsx), before ContextProvider
 * initialises, so the context hooks read the sanitised values on first render
 * (no flash, no effect-ordering race).
 */

const ACTIVE_BREADCRUMBS_KEY = "_zenvoya_active_breadcrumbs";
const BREADCRUMBS_LIST_KEY = "_zenvoya_breadcrumbs_list";
const SCREEN_KEY = "screen_details";

/**
 * MASTER SWITCH — currently OFF (product decision, Aug 2026).
 *
 * With this `false`, a refresh on the review/traveller/payment step is left ALONE:
 * the persisted crumb is not rolled back, the user is not bounced to Home, and no
 * "Your booking session expired" toast fires. Flip to `true` to restore the guard.
 *
 * ⚠️ Turning it off re-exposes what it was written for — see the header comment: the
 * wizard's working state past the search step (`reviewSteps`, the traveller form, the
 * Stripe form) is ephemeral component `useState`, and the backend prebook/cart session
 * is short-lived. So a hard reload can land on a review that renders blank, with
 * `useReviewLeaveBackTrap` swallowing browser Back. Genuinely persisting that state is
 * separate work; this switch only stops the bounce-to-Home.
 *
 * NOT an appConfig flag on purpose: `sanitizeStaleBookingSession()` runs synchronously
 * in index.tsx BEFORE ContextProvider initialises (that ordering is load-bearing — see
 * the header), so no React/appConfig value is readable at that point.
 */
export const BOOKING_SESSION_EXPIRY_ENABLED = false;

/** One-shot flag read by useBookingSessionExpiredToast once the app mounts. */
export const BOOKING_SESSION_EXPIRED_FLAG = "_zenvoya_booking_session_expired";

// Steps beyond the search lists (Flights / Stays) whose state cannot be
// rehydrated after a hard reload. Search lists are intentionally excluded —
// they re-render from persisted search results (or re-search) and are safe to
// resume.
const NON_RESUMABLE_STEPS = new Set<string>([
  BOOKING_IN_PROGRESS_BREADCRUMBS_TEXT.Review,
  BOOKING_IN_PROGRESS_BREADCRUMBS_TEXT.ReviewNew,
  BOOKING_IN_PROGRESS_BREADCRUMBS_TEXT.Travelers,
  BOOKING_IN_PROGRESS_BREADCRUMBS_TEXT.TravelersFlight,
  BOOKING_IN_PROGRESS_BREADCRUMBS_TEXT.TravelersHotel,
  BOOKING_IN_PROGRESS_BREADCRUMBS_TEXT.Payment,
]);

export function sanitizeStaleBookingSession(): void {
  // Feature-flagged off — the review step is left exactly as persisted on refresh.
  if (!BOOKING_SESSION_EXPIRY_ENABLED) return;
  if (typeof window === "undefined") return;
  try {
    // getItemFromSessionStorage transparently handles the LOCAL (plain JSON) vs
    // deployed (encrypted) storage formats — do NOT hand-roll JSON.parse here.
    const activeCrumb = getItemFromSessionStorage(ACTIVE_BREADCRUMBS_KEY);
    const activeValue: unknown = activeCrumb?.value;

    if (typeof activeValue !== "string" || !NON_RESUMABLE_STEPS.has(activeValue)) {
      return;
    }

    // Roll the wizard back to the first step and clear the crumb list...
    setItemInSessionStorage(ACTIVE_BREADCRUMBS_KEY, INITIAL_BREADCRUMBS_LIST[0]);
    // ...breadList is backed by localStorage (useSessionStorage), so reset it there.
    setItemInLocalStorage(BREADCRUMBS_LIST_KEY, INITIAL_BREADCRUMBS_LIST);
    // Drop the booking-panel screen so desktop shows Home, not the dead review.
    window.sessionStorage.removeItem(SCREEN_KEY);

    // Mobile keeps the review on the full-page /booking route; the screen reset
    // alone won't move it, so rewrite the entry URL to Home before React Router
    // boots. Desktop review lives on Home already (screen-gated), so it needs
    // no rewrite.
    if (window.location.pathname === ROUTES.BookingInProgress) {
      window.history.replaceState(null, "", ROUTES.Home);
    }

    try {
      window.sessionStorage.setItem(BOOKING_SESSION_EXPIRED_FLAG, "1");
    } catch {
      /* flag is best-effort; the recovery above is what matters */
    }
  } catch {
    // Never let a storage hiccup block app boot.
  }
}
